import React, { useState, useEffect, forwardRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  useTheme,
  CircularProgress,
  Slide,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { tokens } from "../../theme";
import * as ProductService from "../../services/ProductService";

// Transition for the Dialog (slides up)
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ProductHistoryModal = ({ open, handleClose, product }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && product?.id) {
      setLoading(true);

      // Check if the endpoint exists in your ProductService yet
      if (typeof ProductService.getProductHistory === "function") {
        ProductService.getProductHistory(product.id)
          .then((res) => {
            const data = (res.data || []).map((item, index) => ({
              id: item.id || index,
              ...item,
            }));
            setHistoryData(data);
          })
          .catch((err) => {
            console.error("Failed to load history", err);
            setHistoryData([]);
          })
          .finally(() => setLoading(false));
      } else {
        // Fallback mock data so you can preview the UI before the backend is ready
        console.warn(
          "getProductHistory is not exported from ProductService yet. Showing mock data.",
        );
        setTimeout(() => {
          setHistoryData([
            {
              id: 1,
              updatedAt: new Date().toISOString(),
              updatedBy: "Admin",
              action: "Updated quantity from 20 to 50",
            },
            {
              id: 2,
              updatedAt: new Date(Date.now() - 86400000).toISOString(),
              updatedBy: "System",
              action: "Product originally created",
            },
          ]);
          setLoading(false);
        }, 600);
      }
    } else {
      setHistoryData([]);
    }
  }, [open, product]);

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${dd}-${mm}-${yyyy} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
      keepMounted
      PaperProps={{
        sx: {
          backgroundColor:
            theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
          backgroundImage: "none",
          borderRadius: "16px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
          border: `1px solid ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.05)"
          }`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pt: 3,
          pb: 2,
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(99, 102, 241, 0.15)"
                  : "rgba(79, 70, 229, 0.1)",
              p: 1,
              borderRadius: "8px",
              display: "flex",
            }}
          >
            <HistoryOutlinedIcon
              sx={{ color: colors.blueAccent[500], fontSize: "24px" }}
            />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight="800" color={colors.gray[100]}>
              Update History
            </Typography>
            <Typography variant="caption" color={colors.gray[400]}>
              {product?.productName}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            color: colors.gray[400],
            transition: "all 0.2s ease",
            "&:hover": {
              color: colors.redAccent[500],
              transform: "rotate(90deg)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider
        sx={{
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.08)",
        }}
      />

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, minHeight: "300px" }}>
        {loading ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            height="100%"
            minHeight="250px"
          >
            <CircularProgress sx={{ color: colors.blueAccent[500] }} />
            <Typography mt={2} color={colors.gray[400]} variant="body2">
              Loading history...
            </Typography>
          </Box>
        ) : historyData.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            height="100%"
            minHeight="250px"
          >
            <HistoryOutlinedIcon
              sx={{ fontSize: 48, color: colors.gray[500], mb: 2 }}
            />
            <Typography color={colors.gray[300]} variant="h6">
              No history found
            </Typography>
          </Box>
        ) : (
          <Box sx={{ animation: "fadeIn 0.4s ease-out" }}>
            {historyData.map((item, index) => (
              <Box key={item.id} display="flex" gap={2} mb={3}>
                {/* Timeline Axis */}
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: colors.blueAccent[500],
                      boxShadow: `0 0 0 4px ${
                        theme.palette.mode === "dark"
                          ? "rgba(99, 102, 241, 0.2)"
                          : "rgba(79, 70, 229, 0.15)"
                      }`,
                      mt: "6px",
                    }}
                  />
                  {index !== historyData.length - 1 && (
                    <Box
                      sx={{
                        flex: 1,
                        width: 2,
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.1)"
                            : "rgba(0,0,0,0.08)",
                        mt: 1,
                        mb: -2, // Pulls the line down to the next dot
                      }}
                    />
                  )}
                </Box>

                {/* Content Card */}
                <Box
                  flex={1}
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.02)"
                        : "rgba(0,0,0,0.01)",
                    border: `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.05)"
                    }`,
                    borderRadius: "12px",
                    p: 2,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "rgba(0,0,0,0.03)",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    fontWeight="700"
                    color={colors.gray[100]}
                    mb={1}
                  >
                    {item.action}
                  </Typography>

                  <Box
                    display="flex"
                    flexWrap="wrap"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                  >
                    <Typography
                      variant="caption"
                      color={colors.gray[400]}
                      fontWeight="500"
                    >
                      {formatDateTime(item.updatedAt)}
                    </Typography>

                    <Box display="flex" alignItems="center" gap={0.5}>
                      <PersonOutlineOutlinedIcon
                        sx={{ fontSize: "14px", color: colors.gray[500] }}
                      />
                      <Typography
                        variant="caption"
                        color={colors.gray[300]}
                        fontWeight="600"
                      >
                        {item.updatedBy || "System"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Dialog>
  );
};

export default ProductHistoryModal;
